(() => {
  'use strict';

  const GLOBAL_KEY = '__rfParallaxHeroController';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].scan(document);
    return;
  }

  const instances = new Map();
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  class RFParallaxHero {
    constructor(element) {
      this.element = element;
      this.media = element.querySelector('.rf-parallax-hero__media');
      this.enabled = element.dataset.parallaxEnabled === 'true';
      this.mobileStrength = Number(element.dataset.parallaxMobile || 0);
      this.desktopStrength = Number(element.dataset.parallaxDesktop || 0);
      this.viewportHeight = window.innerHeight || 1;
      this.mediaHeight = this.media ? this.media.offsetHeight : element.offsetHeight;
      this.rafId = 0;
      this.resizeRaf = 0;
      this.listening = false;
      this.destroyed = false;
      this.isNearViewport = false;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onIntersection = this.onIntersection.bind(this);
      this.onMotionPreference = this.onMotionPreference.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onVisualViewportResize = this.onVisualViewportResize.bind(this);

      if (!this.enabled || reducedMotionQuery.matches) {
        this.reset();
        return;
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(this.onIntersection, {
          root: null,
          rootMargin: '40% 0px 40% 0px',
          threshold: 0
        });
        this.observer.observe(this.element);
      } else {
        this.isNearViewport = true;
        this.startListening();
      }

      if (typeof reducedMotionQuery.addEventListener === 'function') {
        reducedMotionQuery.addEventListener('change', this.onMotionPreference);
      }

      if (typeof desktopQuery.addEventListener === 'function') {
        desktopQuery.addEventListener('change', this.onBreakpointChange);
      }

      if (window.visualViewport && typeof window.visualViewport.addEventListener === 'function') {
        window.visualViewport.addEventListener('resize', this.onVisualViewportResize, { passive: true });
      }
    }

    getStrength() {
      return desktopQuery.matches ? this.desktopStrength : this.mobileStrength;
    }

    refreshMetrics() {
      this.viewportHeight = Math.max(
        window.visualViewport ? window.visualViewport.height : 0,
        window.innerHeight || 0,
        1
      );
      this.mediaHeight = Math.max(this.media ? this.media.offsetHeight : 0, this.element.offsetHeight, 1);
    }

    onIntersection(entries) {
      const entry = entries[0];
      if (!entry) return;

      this.isNearViewport = entry.isIntersecting;
      if (this.isNearViewport) {
        this.startListening();
      } else {
        this.stopListening();
      }
    }

    onMotionPreference(event) {
      if (event.matches) {
        this.stopListening();
        this.reset();
        return;
      }

      if (this.enabled && this.isNearViewport && this.getStrength() > 0) {
        this.startListening();
      }
    }

    onBreakpointChange() {
      this.refreshMetrics();
      if (this.getStrength() <= 0) {
        this.stopListening();
        this.reset();
      } else if (this.enabled && this.isNearViewport && !reducedMotionQuery.matches) {
        this.startListening();
      }
    }

    onVisualViewportResize() {
      this.onResize();
    }

    startListening() {
      if (this.listening || this.destroyed || !this.enabled || reducedMotionQuery.matches || this.getStrength() <= 0) return;

      this.refreshMetrics();
      this.update();
      this.listening = true;
      this.element.classList.add('is-parallax-active');
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    stopListening() {
      if (!this.listening) return;
      this.listening = false;
      this.element.classList.remove('is-parallax-active');
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = 0;
      }
    }

    onScroll() {
      this.schedule();
    }

    onResize() {
      // Mobile browser chrome can emit resize + visualViewport resize together
      // while the page is moving. Collapse those bursts into one frame so we do
      // not force repeated layout reads during a scroll gesture.
      if (this.resizeRaf || this.destroyed) return;
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;
        this.refreshMetrics();
        if (this.getStrength() <= 0) {
          this.stopListening();
          this.reset();
          return;
        }
        this.schedule();
      });
    }

    schedule() {
      if (this.rafId || this.destroyed) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0;
        this.update();
      });
    }

    update() {
      if (reducedMotionQuery.matches || !this.enabled) {
        this.reset();
        return;
      }

      const strength = this.getStrength();
      if (strength <= 0) {
        this.reset();
        return;
      }

      const rect = this.element.getBoundingClientRect();
      const viewportHeight = Math.max(this.viewportHeight, 1);
      const heroHeight = Math.max(rect.height, 1);

      // Equivalent to Motion's offset: ["start end", "end start"].
      const progress = Math.min(
        1,
        Math.max(0, (viewportHeight - rect.top) / (viewportHeight + heroHeight))
      );

      // Counter the section's normal page movement so the media behaves like the
      // reference's viewport-fixed layer, while remaining clipped/self-contained.
      const pinY = -rect.top;

      // Add the small 0 -> N% drift from the reference on top of the pinned layer.
      const driftY = progress * this.mediaHeight * (strength / 100);

      this.element.style.setProperty('--rf-pin-y', `${pinY.toFixed(2)}px`);
      this.element.style.setProperty('--rf-parallax-y', `${driftY.toFixed(2)}px`);
    }

    reset() {
      this.element.style.setProperty('--rf-pin-y', '0px');
      this.element.style.setProperty('--rf-parallax-y', '0px');
      this.element.classList.remove('is-parallax-active');
    }

    destroy() {
      this.destroyed = true;
      this.stopListening();
      if (this.observer) this.observer.disconnect();
      if (typeof reducedMotionQuery.removeEventListener === 'function') {
        reducedMotionQuery.removeEventListener('change', this.onMotionPreference);
      }
      if (typeof desktopQuery.removeEventListener === 'function') {
        desktopQuery.removeEventListener('change', this.onBreakpointChange);
      }
      if (window.visualViewport && typeof window.visualViewport.removeEventListener === 'function') {
        window.visualViewport.removeEventListener('resize', this.onVisualViewportResize);
      }
      if (this.resizeRaf) {
        cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = 0;
      }
      this.reset();
    }
  }

  const controller = {
    scan(root = document) {
      const candidates = [];
      if (root.matches && root.matches('[data-rf-parallax-hero]')) candidates.push(root);
      if (root.querySelectorAll) candidates.push(...root.querySelectorAll('[data-rf-parallax-hero]'));

      candidates.forEach((element) => {
        if (!instances.has(element)) instances.set(element, new RFParallaxHero(element));
      });
    },

    destroyTree(root) {
      instances.forEach((instance, element) => {
        if (element === root || (root.contains && root.contains(element))) {
          instance.destroy();
          instances.delete(element);
        }
      });
    }
  };

  window[GLOBAL_KEY] = controller;

  const boot = () => controller.scan(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => controller.scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => controller.destroyTree(event.target));
})();
